// Social Pulse — Confidence Scoring + Regional Lenses.
//
// Pure-logic module. Implements PROTOCOL_SPEC.md §4:
//   - Confidence Weight table (CW)      ← tier-keyed, independent of Power Multiplier
//   - Confidence formula                ← (verified-weighted stake) / (total-weighted stake)
//   - Star rating buckets               ← 1..5 in fixed 20% bands
//   - Contextual band labels            ← CITIZEN VERIFIED / UNVERIFIED / null
//   - Regional lenses                   ← LOCAL / GLOBAL / ASIA / EUROPE filter
//
// The sandbox ticker simulates a crowd of pledges per headline; a live
// build would swap `simulateCrowd` for a query against the Social Pulse
// feed. Everything downstream reads the same scoring helpers.

import { IdentityLevel, IdentityTier } from './identity';

// ─── Confidence weight ──────────────────────────────────────────────────

export const CONFIDENCE_WEIGHT_BY_TIER: Record<IdentityTier, number> = {
  [IdentityLevel.SEEKER]:    0.25,
  [IdentityLevel.OBSERVER]:  1.0,
  [IdentityLevel.CITIZEN]:   2.0,
  [IdentityLevel.SOVEREIGN]: 3.0,
};

export function confidenceWeightForTier(tier: IdentityTier): number {
  return CONFIDENCE_WEIGHT_BY_TIER[tier] ?? 1.0;
}

// ─── Pledge shape + scoring ─────────────────────────────────────────────

export interface PulsePledge {
  tier:  IdentityTier;
  stake: number;   // OC
}

// Returns Confidence in [0, 1]. Empty pledge set → 0 (nothing to back
// the headline yet).
export function confidenceScore(pledges: PulsePledge[]): number {
  let num = 0;
  let den = 0;
  for (const p of pledges) {
    const cw = confidenceWeightForTier(p.tier);
    const w  = p.stake * cw;
    den += w;
    if (p.tier === IdentityLevel.CITIZEN || p.tier === IdentityLevel.SOVEREIGN) {
      num += w;
    }
  }
  if (den <= 0) return 0;
  return num / den;
}

// 1..5 stars, fixed 20% bands per spec. Minimum 1 star always shown so
// empty pulses still render a row.
export function starRating(score: number): 1 | 2 | 3 | 4 | 5 {
  if (score >= 0.80) return 5;
  if (score >= 0.60) return 4;
  if (score >= 0.40) return 3;
  if (score >= 0.20) return 2;
  return 1;
}

export type ConfidenceBand = 'verified' | 'volatile' | 'neutral';

export function confidenceBand(score: number): ConfidenceBand {
  // Thresholds are protocol-level (§4). The volatile band MUST fire at
  // strictly below 30% — both SocialPulseTicker and PledgeCardGrid key
  // their red UNVERIFIED label off this band, and the v1.3 background
  // activity is calibrated to land noisy markets here.
  if (score >= 0.80) return 'verified';
  if (score <  0.30) return 'volatile';
  return 'neutral';
}

export function confidenceLabel(score: number): string | null {
  const band = confidenceBand(score);
  if (band === 'verified') return 'CITIZEN VERIFIED';
  if (band === 'volatile') return 'UNVERIFIED / VOLATILE';
  return null;
}

// ─── Regional lenses ────────────────────────────────────────────────────
// The public API ships four lenses. `ALL` is a sentinel used by the
// ticker when the viewer hasn't picked a region yet — it bypasses the
// filter and draws from the full pool.

export type RegionLens = 'LOCAL' | 'GLOBAL' | 'ASIA' | 'EUROPE';

export interface LensOption {
  value: RegionLens;
  label: string;    // what the dropdown shows
  short: string;    // what the headline row shows
}

export const LENS_OPTIONS: LensOption[] = [
  { value: 'LOCAL',  label: 'Local',             short: 'LOCAL'   },
  { value: 'GLOBAL', label: 'Global · Macro',    short: 'MACRO'   },
  { value: 'ASIA',   label: 'Regional · Asia',   short: 'ASIA'    },
  { value: 'EUROPE', label: 'Regional · Europe', short: 'EUROPE'  },
];

// ─── Headline pool ──────────────────────────────────────────────────────

interface HeadlineTemplate {
  regions: RegionLens[];   // multi-lens tag array per §5
  text:    string;
}

// Hand-authored so the sandbox feels grounded rather than
// Lorem-Ipsum-random. Each line is plausible copy a Newswire feed
// might surface; picking from a finite pool also makes QA easier
// (testers can match a known headline → a known rough score).
//
// Most lines are single-lens; a handful carry two tags where the
// signal is genuinely multi-regional (ECB and BOJ both show up under
// GLOBAL macro as well as their home region).
const HEADLINES: HeadlineTemplate[] = [
  // ── LOCAL ── (Arena seed-aligned; no UK-specific content per
  //             DECISIONS.md 2026-05-05 / IDEAS.md I-010)
  { regions: ['LOCAL'],           text: 'Sierra Space Confirms Dream Chaser Final Stack Tomorrow'    },
  { regions: ['LOCAL'],           text: 'Coinbase BTC Volume Hits 18-Month High Above $120k'        },
  { regions: ['LOCAL'],           text: 'ECB Minutes: Three-Way Split on September Cut Decision'    },
  { regions: ['LOCAL'],           text: 'NOAA Trims 2026 Hurricane Forecast to 19 Named Storms'     },
  { regions: ['LOCAL'],           text: 'Bookmakers Tighten Real Madrid Final Odds to 13/8'         },
  { regions: ['LOCAL', 'EUROPE'], text: 'NASA SMD Names DAVINCI Atmospheric Probe to PDR Phase'     },

  // ── GLOBAL (Macro) ──
  { regions: ['GLOBAL'],          text: 'Gold Breaks $3,100 as Yen Slides Below 200 to the Dollar'  },
  { regions: ['GLOBAL'],          text: 'US 10-Year Yields Edge to 4.8% on Hot CPI Print'           },
  { regions: ['GLOBAL'],          text: 'Brent Crude Holds $92 Despite OPEC+ Quota Rumours'         },
  { regions: ['GLOBAL'],          text: 'Base Hits 2M Daily Active Users Milestone'                 },
  { regions: ['GLOBAL'],          text: 'Ethereum Pectra Upgrade Schedule Narrowed to Q2'           },
  { regions: ['GLOBAL'],          text: 'IMF Revises Global Growth Outlook Upward for 2026'         },

  // ── ASIA ──
  { regions: ['ASIA', 'GLOBAL'],  text: 'BOJ Holds Rates; Yen Traders Watch 200-Handle for Intervention' },
  { regions: ['ASIA'],            text: 'Nikkei 225 Closes Above 42,000 on Tech Rebound'            },
  { regions: ['ASIA'],            text: 'T1 Upsets Gen.G 3–2 in LCK Regional Finals'                },
  { regions: ['ASIA'],            text: 'Singapore GP Green-Lights Night-Race Schedule Change'      },
  { regions: ['ASIA'],            text: 'India vs Australia: 4th ODI Moved to Kolkata After Rain'   },
  { regions: ['ASIA', 'GLOBAL'],  text: 'China Manufacturing PMI Prints 50.2, First Expansion in 6 Months' },

  // ── EUROPE ──
  { regions: ['EUROPE', 'GLOBAL'], text: 'ECB Hints at Surprise Rate Cut at Next Quarterly Meeting' },
  { regions: ['EUROPE'],           text: 'Eurozone CPI Print Steady at 2.4% Year-on-Year'           },
  { regions: ['EUROPE'],           text: 'Juventus Signs 19-Year-Old Playmaker From Bundesliga'     },
  { regions: ['EUROPE'],           text: 'German Bund Auction Draws Strongest Cover in 18 Months'   },
  { regions: ['EUROPE'],           text: 'French Parliament Passes Digital Markets Amendment'       },
  { regions: ['EUROPE'],           text: 'North Sea Offshore Wind Capacity Grows 12% YoY'           },
];

// ─── Headline generation ────────────────────────────────────────────────

export interface Headline {
  id:         string;
  text:       string;
  regions:    RegionLens[];       // §5 tag array — may contain 1..N lenses
  timestamp:  string;             // ISO
  crowd:      PulsePledge[];      // the simulated pledges backing this headline
  score:      number;             // cached confidenceScore(crowd) in [0,1]
  volumeOC:   number;             // synthetic notional in OC for the terminal row
  deltaPct:   number;             // synthetic 24h delta for the terminal row
}

// Random in [lo, hi).
function rand(lo: number, hi: number): number {
  return lo + Math.random() * (hi - lo);
}

// Rough but realistic tier mix — a world where most citizens haven't
// verified yet. Tweakable; the mix is what makes individual headlines
// *feel* different (sometimes verified-heavy, sometimes noise-heavy).
const TIER_DISTRIBUTION: Array<{ tier: IdentityTier; p: number }> = [
  { tier: IdentityLevel.SEEKER,    p: 0.30 },
  { tier: IdentityLevel.OBSERVER,  p: 0.45 },
  { tier: IdentityLevel.CITIZEN,   p: 0.20 },
  { tier: IdentityLevel.SOVEREIGN, p: 0.05 },
];

function drawTier(): IdentityTier {
  const r = Math.random();
  let acc = 0;
  for (const bucket of TIER_DISTRIBUTION) {
    acc += bucket.p;
    if (r < acc) return bucket.tier;
  }
  return IdentityLevel.OBSERVER;
}

// Each headline gets a fresh synthetic crowd of 8–24 pledges. Stakes
// are uniformly distributed between 5 and 250 OC — enough variance
// that a single whale can sway a row.
export function simulateCrowd(size = Math.floor(rand(8, 24))): PulsePledge[] {
  const out: PulsePledge[] = [];
  for (let i = 0; i < size; i++) {
    out.push({ tier: drawTier(), stake: Math.round(rand(5, 250)) });
  }
  return out;
}

// Chance that a generated headline will be explicitly biased toward
// verified or volatile territory. Having a small chance of "seeded"
// rows prevents every row trending to the population mean and makes
// the ticker visibly interesting: sometimes the crowd is clearly
// verified, sometimes clearly not.
const BIAS_CHANCE = 0.35;

function simulateBiasedCrowd(): PulsePledge[] {
  const base = simulateCrowd();
  if (Math.random() >= BIAS_CHANCE) return base;
  if (Math.random() < 0.5) {
    // Verified seeding: inject 3–5 Citizen/Sovereign whales.
    const n = Math.floor(rand(3, 6));
    for (let i = 0; i < n; i++) {
      base.push({
        tier:  Math.random() < 0.75 ? IdentityLevel.CITIZEN : IdentityLevel.SOVEREIGN,
        stake: Math.round(rand(100, 400)),
      });
    }
  } else {
    // Noise seeding: inject 4–8 Seeker entries.
    const n = Math.floor(rand(4, 9));
    for (let i = 0; i < n; i++) {
      base.push({ tier: IdentityLevel.SEEKER, stake: Math.round(rand(10, 80)) });
    }
  }
  return base;
}

let idCounter = 0;
function mintHeadlineId(): string {
  idCounter += 1;
  return `hl_${Date.now().toString(36)}_${idCounter}`;
}

function pickTemplate(lens?: RegionLens): HeadlineTemplate {
  const pool = lens ? HEADLINES.filter(h => h.regions.includes(lens)) : HEADLINES;
  const src  = pool.length > 0 ? pool : HEADLINES;
  return src[Math.floor(Math.random() * src.length)];
}

export function generateHeadline(lens?: RegionLens): Headline {
  const tmpl  = pickTemplate(lens);
  const crowd = simulateBiasedCrowd();
  const totalStake = crowd.reduce((sum, p) => sum + p.stake, 0);
  return {
    id:        mintHeadlineId(),
    text:      tmpl.text,
    regions:   tmpl.regions,
    timestamp: new Date().toISOString(),
    crowd,
    score:     confidenceScore(crowd),
    volumeOC:  totalStake,
    deltaPct:  +(rand(-8, 8)).toFixed(2),
  };
}
